
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function CreateTicket() {
  const navigate = useNavigate();
  const descriptionRef = useRef(null);

  // =========================================================
  // LOGGED-IN USER
  // =========================================================
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // =========================================================
  // FORM
  // =========================================================
  const [form, setForm] = useState({
    name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
    email: user.email || "",
    mobile: user.mobile || "",
    alt_mobile: "",
    contact_pref: "",
    consent: false,
    device_type: "",
    os: "",
    brand: "",
    device_age: "",
    subject: "",
    description: "",
    category: "",
    priority: "Medium",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  // =========================================================
  // HANDLE CHANGE
  // =========================================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = type === "checkbox" ? checked : value;

    // MOBILE NUMBERS: only numbers, maximum 10 digits
    if (name === "mobile" || name === "alt_mobile") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // DEVICE AGE: only numbers, maximum 3 digits
    if (name === "device_age") {
      newValue = value.replace(/\D/g, "").slice(0, 3);
    }

    // SUBJECT: maximum 255 characters
    if (name === "subject") {
      newValue = value.slice(0, 255);
    }

    // DESCRIPTION: maximum 1000 characters
    if (name === "description") {
      newValue = value.slice(0, 1000);
    }

    // CURRENT MOBILE VALUES
    const currentMobile =
      name === "mobile" ? newValue : form.mobile;

    const currentAltMobile =
      name === "alt_mobile" ? newValue : form.alt_mobile;

    // UPDATE FORM
    setForm((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    
    // AUTOMATICALLY FOCUS DESCRIPTION WHEN OTHERS IS SELECTED
if (name === "category" && newValue === "Others") {
  setTimeout(() => {
    descriptionRef.current?.focus();
  }, 0);
}

    // CLEAR CURRENT FIELD ERROR
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    // DUPLICATE MOBILE VALIDATION
    if (
      (name === "mobile" || name === "alt_mobile") &&
      currentMobile &&
      currentAltMobile &&
      currentMobile.length === 10 &&
      currentAltMobile.length === 10 &&
      currentMobile === currentAltMobile
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        alt_mobile:
          "Alternate mobile number cannot be the same as primary mobile number",
      }));
    }
  };

  // =========================================================
  // SCROLL TO FIRST INVALID FIELD
  // =========================================================
  const scrollToFirstError = (errorFields) => {
    const firstErrorField = Object.keys(errorFields).find(
      (fieldName) => Boolean(errorFields[fieldName])
    );

    if (!firstErrorField) return;

    requestAnimationFrame(() => {
      const field = document.querySelector(
        `[name="${firstErrorField}"]`
      );

      if (field) {
        field.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        field.focus({ preventScroll: true });
      }
    });
  };

  // =========================================================
  // VALIDATION
  // =========================================================
  const validate = () => {
    const err = {};

    // NAME
    const name = form.name.trim().replace(/\s+/g, " ");

    if (!name) {
      err.name = "Name is required";
    } else if (!/^[A-Za-z ]+$/.test(name)) {
      err.name = "Only letters and spaces are allowed";
    } else if (name.length < 3) {
      err.name = "Minimum 3 characters required";
    } else if (name.length > 100) {
      err.name = "Maximum 100 characters allowed";
    }

    // EMAIL
    const email = form.email.trim();

    if (!email) {
      err.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      err.email = "Invalid email";
    }

    // PRIMARY MOBILE
    const mobile = form.mobile.trim();

    if (!mobile) {
      err.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(mobile)) {
      err.mobile = "Enter a valid 10-digit mobile number";
    }

    // ALTERNATE MOBILE
    const altMobile = form.alt_mobile.trim();

    if (altMobile) {
      if (!/^[6-9]\d{9}$/.test(altMobile)) {
        err.alt_mobile =
          "Enter a valid 10-digit alternate mobile number";
      }
    }

    // DUPLICATE MOBILE CHECK
    if (mobile && altMobile && mobile === altMobile) {
      err.alt_mobile =
        "Alternate mobile number cannot be the same as primary mobile number";
    }

    // DEVICE TYPE
    if (!form.device_type) {
      err.device_type = "Select device type";
    }

    // OPERATING SYSTEM
    if (!form.os) {
      err.os = "Select operating system";
    }

    // BRAND
    const brand = form.brand.trim();

    if (brand.length > 100) {
      err.brand = "Maximum 100 characters allowed";
    } else if (brand && !/^[A-Za-z0-9 .-]+$/.test(brand)) {
      err.brand = "Invalid brand name";
    }

    // DEVICE AGE
    if (form.device_age) {
      const deviceAge = Number(form.device_age);

      if (
        isNaN(deviceAge) ||
        deviceAge < 0 ||
        deviceAge > 30
      ) {
        err.device_age =
          "Enter a valid device age between 0 and 30";
      }
    }

    // SUBJECT
    const subject = form.subject.trim();

    if (!subject) {
      err.subject = "Subject is required";
    } else if (subject.length < 5) {
      err.subject = "Minimum 5 characters required";
    } else if (subject.length > 255) {
      err.subject = "Maximum 255 characters allowed";
    }

    // DESCRIPTION
    const description = form.description.trim();

    if (!description) {
      err.description = "Description is required";
    } else if (description.length < 20) {
      err.description = "Minimum 20 characters required";
    } else if (description.length > 1000) {
      err.description = "Maximum 1000 characters allowed";
    }

    // CATEGORY
    if (!form.category) {
      err.category = "Category is required";
    }

    // REMOTE ACCESS CONSENT
    if (!form.consent) {
      err.consent = "Please authorize remote access";
    }

    // SET ERRORS AND SCROLL TO FIRST INVALID FIELD
    setErrors(err);

    if (Object.keys(err).length > 0) {
      scrollToFirstError(err);
      return false;
    }

    return true;
  };

  // =========================================================
  // SUBMIT
  // =========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");

    // VALIDATE
    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      // PAYLOAD
      const payload = {
        user_id: user.id,
        ...form,

        name: form.name.trim().replace(/\s+/g, " "),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        alt_mobile: form.alt_mobile?.trim() || "",
        contact_pref: form.contact_pref || "",
        device_type: form.device_type,
        os: form.os,
        brand: form.brand?.trim() || "",
        device_age: String(form.device_age || "").trim(),
        subject: form.subject.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority || "Medium",
        consent: Boolean(form.consent),
      };

      // FINAL DUPLICATE SAFETY CHECK
      if (
        payload.mobile &&
        payload.alt_mobile &&
        payload.mobile === payload.alt_mobile
      ) {
        const duplicateErrors = {
          alt_mobile:
            "Alternate mobile number cannot be the same as primary mobile number",
        };

        setErrors((prevErrors) => ({
          ...prevErrors,
          ...duplicateErrors,
        }));

        scrollToFirstError(duplicateErrors);
        return;
      }

      // DEBUG
      console.log("Logged-in User:", user);
      console.log("Ticket Payload:", payload);

      // API REQUEST
      // Keep this URL if you are testing your local CodeIgniter API.
      const response = await axios.post(
        "https://support.g6techcare.com/api/tickets",
        payload
      );

      // RESPONSE
      console.log("Ticket Response:", response.data);

      // SUCCESS
      setSuccess("Ticket created successfully.");

      // REDIRECT
      setTimeout(() => {
        navigate("/my-tickets");
      }, 2000);
    } catch (err) {
      // ERROR LOG
      console.error("FULL ERROR:", err);
      console.error("API ERROR:", err.response?.data);

      // BACKEND VALIDATION ERRORS
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;

        setErrors(backendErrors);
        scrollToFirstError(backendErrors);
      }

      // BACKEND MESSAGE
      else if (err.response?.data?.message) {
        alert(err.response.data.message);
      }

      // NETWORK / UNKNOWN ERROR
      else {
        alert("Unable to create ticket. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // UI
  // =========================================================
  return (
    <form className="ticket-form" onSubmit={handleSubmit}>
      <h2>Create Ticket</h2>

      {/* SUCCESS MESSAGE */}
      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {/* CUSTOMER INFORMATION */}
      <div className="form-section">
        <h3>Customer Information</h3>

        <div className="grid-2">
          {/* NAME */}
          <div className="field">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
            />
            <span className="error">{errors.name}</span>
          </div>

          {/* EMAIL */}
          <div className="field">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
            />
            <span className="error">{errors.email}</span>
          </div>

          {/* MOBILE */}
          <div className="field">
            <label>Mobile *</label>
            <input
              type="tel"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
              maxLength={10}
              inputMode="numeric"
            />
            <span className="error">{errors.mobile}</span>
          </div>

          {/* ALTERNATE MOBILE */}
          <div className="field">
            <label>Alternate Mobile</label>
            <input
              type="tel"
              name="alt_mobile"
              value={form.alt_mobile}
              onChange={handleChange}
              maxLength={10}
              inputMode="numeric"
            />
            <span className="error">{errors.alt_mobile}</span>
          </div>

          {/* CONTACT PREFERENCE */}
          <div className="field">
            <label>Contact Preference</label>
            <select
              name="contact_pref"
              value={form.contact_pref}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option value="Phone">Phone</option>
              <option value="WhatsApp">WhatsApp</option>
            </select>
          </div>

          {/* CONSENT */}
          <div className="checkbox-field">
            <input
              type="checkbox"
              name="consent"
              checked={form.consent}
              onChange={handleChange}
            />
            <span>I authorize remote access *</span>
            <span className="error">{errors.consent}</span>
          </div>
        </div>
      </div>

      {/* DEVICE INFORMATION */}
      <div className="form-section">
        <h3>Device Information</h3>

        <div className="grid-2">
          {/* DEVICE TYPE */}
          <div className="field">
            <label>Device Type *</label>
            <select
              name="device_type"
              value={form.device_type}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option value="Desktop">Desktop</option>
              <option value="Laptop">Laptop</option>
              <option value="Mobile">Tab</option>
            </select>
            <span className="error">{errors.device_type}</span>
          </div>

          {/* OPERATING SYSTEM */}
          <div className="field">
            <label>Operating System *</label>
            <select
              name="os"
              value={form.os}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option value="Windows">Windows</option>
              <option value="macOS">macOS</option>
              <option value="Linux">Linux</option>
              <option value="Android">Android</option>
              <option value="iOS">iOS</option>
            </select>
            <span className="error">{errors.os}</span>
          </div>

          {/* BRAND */}
          <div className="field">
            <label>Brand</label>
            <input
              type="text"
              name="brand"
              value={form.brand}
              onChange={handleChange}
              maxLength={100}
            />
            <span className="error">{errors.brand}</span>
          </div>

          {/* DEVICE AGE */}
          <div className="field">
            <label>Device Age</label>
            <input
              type="text"
              name="device_age"
              value={form.device_age}
              onChange={handleChange}
              maxLength={3}
              inputMode="numeric"
              placeholder="Years"
            />
            <span className="error">{errors.device_age}</span>
          </div>
        </div>
      </div>

      {/* ISSUE DETAILS */}
      <div className="form-section">
        <h3>Issue Details</h3>

        {/* SUBJECT */}
        <div className="field">
          <label>Subject *</label>
          <input
            type="text"
            name="subject"
            value={form.subject}
            onChange={handleChange}
            maxLength={255}
          />
          <span className="error">{errors.subject}</span>
        </div>

        {/* CATEGORY */}
        <div className="field" style={{ margin: "20px 0" }}>
          <label>Category *</label>
          <select 
            name="category"
            value={form.category}
            onChange={handleChange}
          >
            <option value="">Select</option>
            <option value="System Slow">System Slow</option>
            <option value="Virus">Virus</option>
            <option value="Network Issue">Network Issue</option>
            <option value="Others">Others</option>
          </select>
          <span className="error">{errors.category}</span>
        </div>

        {/* DESCRIPTION */}
        <div
          className="field">
          <label>Description *</label>
          <textarea
            ref={descriptionRef}
            style={{
              height: "120px",
            }}
            name="description"
            value={form.description}
            onChange={handleChange}
            maxLength={1000}
          />

          <div
            style={{
              textAlign: "right",
              fontSize: "12px",
            }}
          >
            {form.description.length}/1000
          </div>

          <span className="error">{errors.description}</span>
        </div>

        
      </div>

      {/* SUBMIT */}
      <button
        type="submit"
        className="submit-btn"
        disabled={loading}
      >
        {loading ? "Creating..." : "Create Ticket"}
      </button>
    </form>
  );
}